import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import {
  EXCEL_WEEK_SCHEDULE,
  EXCEL_OPERATIONAL_MONTHS,
  formatWeekDisplayLabel,
  formatMonthDisplayLabel,
  getDefaultExcelYearlyCycle,
} from '../constants/excelWeeks.ts';
import {
  ArrowLeft,
  Target,
  Calendar,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  RefreshCw,
  Sparkles,
  Layers,
  PieChart,
  ArrowRight,
  ListFilter,
  Check,
  Building,
  Search,
  Filter,
} from 'lucide-react';

interface WeeklyTargetItem {
  id?: string;
  weekLabel: string;
  startDate: string;
  endDate: string;
  targetAmount: number;
}

interface MonthlyTargetItem {
  month: number;
  year?: number;
  targetAmount: number;
  weeklyTargets: WeeklyTargetItem[];
}

interface TargetConfigResponse {
  committeeName: string;
  year: number;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
  targetFrequency?: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  yearlyTarget: number;
  monthlyTargets: MonthlyTargetItem[];
}

interface TargetCycleListItem {
  id: string;
  committeeName: string;
  year: number;
  yearlyTarget: number;
  monthlySum: number;
  monthsCount: number;
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
  weeklyStartDate?: string;
  weeklyEndDate?: string;
  weekLabel?: string;
  targetFrequency?: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  updatedAt: string;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// Helper to format currency string (e.g. ₹1,23,456)
const formatCurrencyInput = (val: number | string): string => {
  if (val === undefined || val === null || val === '') return '';
  const digits = String(val).replace(/\D/g, '');
  if (!digits) return '';
  return '₹' + Number(digits).toLocaleString('en-IN');
};

// Helper to parse currency string back to pure number
const parseCurrencyValue = (val: string | number): number => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  const digits = String(val).replace(/\D/g, '');
  return digits ? Number(digits) : 0;
};

// Helper to get number of days in a month (1-based month)
const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

// Helper to format YYYY-MM-DD in local time without UTC timezone shift
const formatDateLocal = (year: number, month: number, day: number): string => {
  const yyyy = String(year);
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Helper to generate calendar weeks mapped from EXCEL_WEEK_SCHEDULE for any given year and month
const generateCalendarWeeksForMonth = (year: number, month: number): Array<{ weekLabel: string; startDate: string; endDate: string }> => {
  const MONTH_MAP: Record<string, number> = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sept': 9, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12,
  };

  const excelMatches = EXCEL_WEEK_SCHEDULE.filter(w => {
    const mNum = MONTH_MAP[w.month];
    if (mNum !== month) return false;
    const sYear = parseInt(w.startDate.split('-')[0], 10);
    const eYear = parseInt(w.endDate.split('-')[0], 10);
    return sYear === year || eYear === year;
  });

  if (excelMatches.length > 0) {
    return excelMatches.map(w => ({
      weekLabel: w.label,
      startDate: w.startDate,
      endDate: w.endDate,
    }));
  }

  // Fallback to standard calendar days if no Excel schedule matches
  const weeks: Array<{ weekLabel: string; startDate: string; endDate: string }> = [];
  const totalDaysInMonth = new Date(year, month, 0).getDate();

  let weekNum = 1;
  let currentDay = 1;

  while (currentDay <= totalDaysInMonth) {
    const startDateObj = new Date(year, month - 1, currentDay);
    const dayOfWeek = startDateObj.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    let endDay = currentDay + daysUntilSunday;
    if (endDay > totalDaysInMonth) {
      endDay = totalDaysInMonth;
    }

    const startStr = formatDateLocal(year, month, currentDay);
    const endStr = formatDateLocal(year, month, endDay);

    weeks.push({
      weekLabel: `week${weekNum}`,
      startDate: startStr,
      endDate: endStr,
    });

    weekNum++;
    currentDay = endDay + 1;
  }

  return weeks;
};

// Helper to generate dynamic month objects between start (month/year) and end (month/year)
const generateCycleMonths = (
  startM: number,
  startY: number,
  endM: number,
  endY: number
): Array<{ month: number; year: number }> => {
  const result: Array<{ month: number; year: number }> = [];
  let curM = Math.max(1, Math.min(12, startM || 1));
  let curY = startY;
  let eM = Math.max(1, Math.min(12, endM || 1));
  let eY = endY;

  if (eY < curY || (eY === curY && eM < curM)) {
    eY = curY + 1;
  }

  let count = 0;
  while (count < 36) {
    result.push({ month: curM, year: curY });
    if (curM === eM && curY === eY) {
      break;
    }
    curM++;
    if (curM > 12) {
      curM = 1;
      curY++;
    }
    count++;
  }
  return result;
};

// =========================================================================================
// CUSTOM THEME-MATCHING DATE RANGE PICKER COMPONENT
// =========================================================================================
interface CustomWeekDateRangePickerProps {
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  otherWeeks: Array<{ startDate: string; endDate: string }>;
  maxDays?: number;
  onUpdate: (startDate: string, endDate: string) => void;
}

export function CustomWeekDateRangePicker({
  year,
  month,
  startDate,
  endDate,
  otherWeeks,
  maxDays = 7,
  onUpdate,
}: CustomWeekDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pickingStep, setPickingStep] = useState<'START' | 'END'>('START');
  const [tempStartDay, setTempStartDay] = useState<number | null>(null);

  const monthName = MONTH_NAMES[month - 1];
  const daysInM = getDaysInMonth(year, month);
  const firstWeekday = new Date(year, month - 1, 1).getDay();

  const parseDayNum = (dateStr: string): number => {
    if (!dateStr) return 1;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = parseInt(parts[2], 10);
      if (!isNaN(d)) return d;
    }
    return 1;
  };

  const startDay = parseDayNum(startDate);
  const endDay = parseDayNum(endDate);
  const daysSpan = Math.max(1, endDay - startDay + 1);

  const isDayReserved = (day: number): boolean => {
    const targetStr = formatDateLocal(year, month, day);
    for (const w of otherWeeks) {
      if (w.startDate && w.endDate) {
        if (targetStr >= w.startDate && targetStr <= w.endDate) {
          return true;
        }
      }
    }
    return false;
  };

  const handleDayClick = (day: number) => {
    if (isDayReserved(day)) return;

    if (pickingStep === 'START' || tempStartDay === null) {
      setTempStartDay(day);
      setPickingStep('END');

      let maxEnd = Math.min(daysInM, day + (maxDays - 1));
      for (let d = day + 1; d <= maxEnd; d++) {
        if (isDayReserved(d)) {
          maxEnd = d - 1;
          break;
        }
      }
      onUpdate(formatDateLocal(year, month, day), formatDateLocal(year, month, maxEnd));
    } else {
      let sDay = tempStartDay;
      let eDay = day;

      if (eDay < sDay) {
        sDay = eDay;
        eDay = Math.min(daysInM, sDay + (maxDays - 1));
      }

      if (eDay - sDay + 1 > maxDays) {
        eDay = sDay + (maxDays - 1);
      }

      for (let d = sDay; d <= eDay; d++) {
        if (isDayReserved(d)) {
          eDay = Math.max(sDay, d - 1);
          break;
        }
      }

      onUpdate(formatDateLocal(year, month, sDay), formatDateLocal(year, month, eDay));
      setPickingStep('START');
      setTempStartDay(null);
      setIsOpen(false);
    }
  };

  const calendarCells: Array<number | null> = [];
  for (let i = 0; i < firstWeekday; i++) {
    calendarCells.push(null);
  }
  for (let d = 1; d <= daysInM; d++) {
    calendarCells.push(d);
  }

  const startShort = `${startDay} ${MONTH_SHORT[month - 1]}`;
  const endShort = `${endDay} ${MONTH_SHORT[month - 1]}`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-black text-slate-900 cursor-pointer shadow-sm transition-all focus:ring-2 focus:ring-slate-900"
      >
        <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
        <span>📅 {startShort} — {endShort} ({daysSpan} days)</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 z-50 bg-slate-900 text-white rounded-2xl p-4 shadow-2xl border border-slate-800 w-72 animate-in fade-in zoom-in-95">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2.5 mb-3">
            <span className="text-xs font-black text-white flex items-center space-x-1.5">
              <Calendar className="w-3.5 h-3.5 text-emerald-400" />
              <span>{monthName} {year}</span>
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Max {maxDays} Days
            </span>
          </div>

          <p className="text-[10px] text-slate-400 mb-2 font-medium">
            {pickingStep === 'START' ? 'Click Start Date for Week' : `Click End Date (max ${maxDays} days from Day ${tempStartDay})`}
          </p>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-black text-slate-400 mb-1.5">
            <span>Su</span>
            <span>Mo</span>
            <span>Tu</span>
            <span>We</span>
            <span>Th</span>
            <span>Fr</span>
            <span>Sa</span>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {calendarCells.map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} />;
              }
              const reserved = isDayReserved(day);
              const isSelected = day >= startDay && day <= endDay;
              const isStart = day === startDay;

              return (
                <button
                  key={`day-${day}`}
                  type="button"
                  disabled={reserved}
                  onClick={() => handleDayClick(day)}
                  className={`h-7 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center ${reserved
                    ? 'bg-slate-800/50 text-slate-600 line-through cursor-not-allowed opacity-40'
                    : isSelected
                      ? 'bg-emerald-500 text-white font-black shadow-sm'
                      : 'hover:bg-slate-800 text-slate-200'
                    } ${isStart ? 'ring-2 ring-emerald-300' : ''}`}
                  title={reserved ? 'Date already reserved by another week' : `Select Day ${day}`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-800 text-[10px] font-semibold text-slate-400">
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>Selected ({daysSpan}d)</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-slate-700"></span>
              <span>Reserved</span>
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-slate-300 font-bold hover:text-white underline cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Dom680TargetSettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Screen View Mode: 'REGISTRY' (Screen 1) or 'TARGET_PLANNER' (Screen 2)
  const [viewMode, setViewMode] = useState<'REGISTRY' | 'TARGET_PLANNER'>('REGISTRY');

  // Registry Filter Tab: 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  const [registryTab, setRegistryTab] = useState<'WEEKLY' | 'MONTHLY' | 'YEARLY'>('WEEKLY');

  // New Form Option 1: Frequency / Period Type (Weekly | Monthly | Yearly)
  const [targetFrequency, setTargetFrequency] = useState<'WEEKLY' | 'MONTHLY' | 'YEARLY'>('MONTHLY');

  // Weekly Date Range Selection State
  const todayStr = new Date().toISOString().split('T')[0];
  const nextWeekStr = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  const [weeklyStartDate, setWeeklyStartDate] = useState<string>(todayStr);
  const [weeklyEndDate, setWeeklyEndDate] = useState<string>(nextWeekStr);
  const [selectedCalendarWeek, setSelectedCalendarWeek] = useState<string>('');
  // Custom Frequency, Week & Month Dropdown Popover State (Matching Admin Portal Single-Select UI)
  const [isFreqSelectOpen, setIsFreqSelectOpen] = useState<boolean>(false);
  const freqSelectRef = useRef<HTMLDivElement>(null);

  const [isWeekSelectOpen, setIsWeekSelectOpen] = useState<boolean>(false);
  const [weekSearchQuery, setWeekSearchQuery] = useState<string>('');
  const weekSelectRef = useRef<HTMLDivElement>(null);

  const [isMonthSelectOpen, setIsMonthSelectOpen] = useState<boolean>(false);
  const [monthSearchQuery, setMonthSearchQuery] = useState<string>('');
  const monthSelectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (freqSelectRef.current && !freqSelectRef.current.contains(target)) {
        setIsFreqSelectOpen(false);
      }
      if (weekSelectRef.current && !weekSelectRef.current.contains(target)) {
        setIsWeekSelectOpen(false);
      }
      if (monthSelectRef.current && !monthSelectRef.current.contains(target)) {
        setIsMonthSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Confirmation Modal Popup State for Clearing Single or All Committee Targets
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    isOpen: boolean;
    type: 'SINGLE_COMMITTEE' | 'CLEAR_ALL';
    committeeName?: string;
  } | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [targetToDelete, setTargetToDelete] = useState<TargetCycleListItem | null>(null);

  // Master Yearly Cycle is locked to Excel sheet span (not editable)
  const excelYearlyCycle = useMemo(() => {
    const excelDefault = getDefaultExcelYearlyCycle();
    try {
      localStorage.setItem('dom680_master_yearly_cycle', JSON.stringify(excelDefault));
    } catch (e) {}
    return excelDefault;
  }, []);
  const masterStartMonth = excelYearlyCycle.startMonth;
  const masterStartYear = excelYearlyCycle.startYear;
  const masterEndMonth = excelYearlyCycle.endMonth;
  const masterEndYear = excelYearlyCycle.endYear;

  const saveYearlyTargetForAllCommittees = async (targetAmt: number, sMonth = masterStartMonth, sYear = masterStartYear, eMonth = masterEndMonth, eYear = masterEndYear) => {
    if (targetAmt <= 0) return;
    const activeCycleMonths = generateCycleMonths(sMonth, sYear, eMonth, eYear);
    const activeCommittees = committeeList && committeeList.length > 0 ? committeeList : ['Engineering', 'Finance', 'Liaison', 'Legal'];

    for (const cName of activeCommittees) {
      await saveMutation.mutateAsync({
        committeeName: cName,
        year: sYear,
        startMonth: sMonth,
        startYear: sYear,
        endMonth: eMonth,
        endYear: eYear,
        yearlyTarget: targetAmt,
        targetFrequency: 'YEARLY',
        monthlyTargets: activeCycleMonths.map(cm => ({
          month: cm.month,
          year: cm.year,
          targetAmount: 0,
          weeklyTargets: [],
        })),
      });
    }
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const mStr = String(m).padStart(2, '0');
    const lastDay = new Date(y, m, 0).getDate();
    return {
      sStr: `${y}-${mStr}-01`,
      eStr: `${y}-${mStr}-${String(lastDay).padStart(2, '0')}`,
      m,
      y,
    };
  };

  const curMonthRange = getCurrentMonthRange();

  const [selectedStartMonth, setSelectedStartMonth] = useState<number>(curMonthRange.m);
  const [selectedStartYear, setSelectedStartYear] = useState<number>(curMonthRange.y);
  const [selectedEndMonth, setSelectedEndMonth] = useState<number>(curMonthRange.m);
  const [selectedEndYear, setSelectedEndYear] = useState<number>(curMonthRange.y);

  const formatInitialMonthDate = (m: number, y: number, isEnd = false) => {
    const excelMonth = EXCEL_OPERATIONAL_MONTHS.find((em) => em.month === m && em.year === y);
    if (excelMonth) return isEnd ? excelMonth.end : excelMonth.start;
    const mStr = String(m).padStart(2, '0');
    if (!isEnd) return `${y}-${mStr}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${mStr}-${String(lastDay).padStart(2, '0')}`;
  };

  const [monthlyStartDate, setMonthlyStartDate] = useState<string>(curMonthRange.sStr);
  const [monthlyEndDate, setMonthlyEndDate] = useState<string>(curMonthRange.eStr);

  const handleMonthlyStartDateChange = (dateStr: string) => {
    setMonthlyStartDate(dateStr);
    if (dateStr) {
      const parts = dateStr.split('-');
      if (parts.length >= 2) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (!isNaN(m) && !isNaN(y)) {
          setSelectedStartMonth(m);
          setSelectedStartYear(y);
          setSelectedEndMonth(m);
          setSelectedEndYear(y);
          setMonthlyEndDate(formatInitialMonthDate(m, y, true));
        }
      }
    }
  };

  const handleMonthlyEndDateChange = (dateStr: string) => {
    setMonthlyEndDate(dateStr);
    if (dateStr) {
      const parts = dateStr.split('-');
      if (parts.length >= 2) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (!isNaN(m) && !isNaN(y)) {
          setSelectedEndMonth(m);
          setSelectedEndYear(y);
        }
      }
    }
  };

  const parseYearMonthFromDateStr = (dateStr: string) => {
    if (!dateStr) return { month: selectedStartMonth, year: selectedStartYear };
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (!isNaN(y) && !isNaN(m)) return { month: m, year: y };
      }
    } else if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const y = parseInt(parts[2], 10);
        const m = parseInt(parts[1], 10);
        if (!isNaN(y) && !isNaN(m)) return { month: m, year: y };
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return { month: d.getMonth() + 1, year: d.getFullYear() };
    }
    return { month: selectedStartMonth, year: selectedStartYear };
  };

  // Yearly Period Selection State
  const [selectedStartYearOnly, setSelectedStartYearOnly] = useState<number>(masterStartYear);
  const [selectedEndYearOnly, setSelectedEndYearOnly] = useState<number>(masterEndYear);

  // Single Selected Committee (Optional filter or focus)
  const [selectedCommittee, setSelectedCommittee] = useState<string>('');

  // Independent Committee Target Maps per Frequency
  const [weeklyCommitteeTargets, setWeeklyCommitteeTargets] = useState<Record<string, number>>({});
  const [monthlyCommitteeTargets, setMonthlyCommitteeTargets] = useState<Record<string, number>>({});
  const [yearlyCommitteeTargets, setYearlyCommitteeTargets] = useState<Record<string, number>>({});

  // Helper to get active committee target for currently selected frequency
  const getCurrentCommitteeTarget = (cName: string) => {
    if (targetFrequency === 'WEEKLY') return weeklyCommitteeTargets[cName] || 0;
    if (targetFrequency === 'YEARLY') return yearlyCommitteeTargets[cName] || 0;
    return monthlyCommitteeTargets[cName] || 0;
  };

  // Screen 2 Planner State
  const [yearlyTarget, setYearlyTarget] = useState<number>(0);
  const [maxDaysPerPeriod, setMaxDaysPerPeriod] = useState<number>(7);
  const [monthlyTargets, setMonthlyTargets] = useState<MonthlyTargetItem[]>([]);
  const [expandedMonthKey, setExpandedMonthKey] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // 1. Fetch available Committees from Department Groups
  const { data: deptGroupsData, refetch: refetchDeptGroups } = useQuery({
    queryKey: ['departmentGroupsList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data;
    },
  });

  const committeeList: string[] = useMemo(
    () =>
      deptGroupsData
        ? deptGroupsData.map((g: any) => g.name)
        : ['ENG. Execution', 'OPERATION', 'QA_test', 'Finance', 'Legal', 'Liaison'],
    [deptGroupsData]
  );

  // Initialize committee target maps when committeeList loads
  useEffect(() => {
    if (committeeList && committeeList.length > 0) {
      const initMap = (prev: Record<string, number>) => {
        const updated = { ...prev };
        for (const c of committeeList) {
          if (updated[c] === undefined) updated[c] = 0;
        }
        return updated;
      };
      setWeeklyCommitteeTargets(initMap);
      setMonthlyCommitteeTargets(initMap);
      setYearlyCommitteeTargets(initMap);
    }
  }, [deptGroupsData]);

  // 2. Fetch All Configured Target Cycles Registry List
  const { data: cyclesList, refetch: refetchCyclesList, isLoading: isCyclesLoading } = useQuery<TargetCycleListItem[]>({
    queryKey: ['dom680TargetConfigsList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/dom680-target-configs-list');
      return res.data.data;
    },
  });

  /** One network round-trip per API (no invalidate — that caused duplicate refetches) */
  const refreshTargetPlannerApis = async (): Promise<TargetCycleListItem[]> => {
    const [cyclesResult] = await Promise.all([
      refetchCyclesList(),
      refetchDeptGroups(),
    ]);
    return (cyclesResult.data || []) as TargetCycleListItem[];
  };

  const resolveCommitteeKey = (name: string, committees: string[]) => {
    const found = committees.find((c) => c.toLowerCase() === (name || '').toLowerCase());
    return found || name;
  };

  /** Fresh committee → amount map for a cycle (replace, never merge with another period). */
  const buildCommitteeTargetsMap = (
    committees: string[],
    items: Array<{ committeeName?: string; yearlyTarget?: number; monthlySum?: number }>
  ) => {
    const map: Record<string, number> = {};
    for (const c of committees) map[c] = 0;
    for (const it of items) {
      if (!it.committeeName) continue;
      const key = resolveCommitteeKey(it.committeeName, committees);
      map[key] = Number(it.yearlyTarget) || Number(it.monthlySum) || 0;
    }
    return map;
  };

  /** Yearly amounts for locked Excel master cycle (by DB year / span start) */
  const buildYearlyTargetsFromList = (
    list: TargetCycleListItem[] | undefined,
    committees: string[],
    startM: number,
    startY: number
  ) => {
    const yMap: Record<string, number> = {};
    for (const c of committees) yMap[c] = 0;
    for (const item of list || []) {
      if (!item.committeeName) continue;
      const itemFreq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
      if (itemFreq !== 'YEARLY') continue;
      const val = Number(item.yearlyTarget) || 0;
      if (val <= 0) continue;
      // Match locked master cycle: DB year key or listed span start
      const matchesMaster =
        Number(item.year) === startY ||
        (item.startMonth === startM && item.startYear === startY);
      if (!matchesMaster) continue;
      yMap[resolveCommitteeKey(item.committeeName, committees)] = val;
    }
    return yMap;
  };

  // Check if Master Yearly Cycle has been completed/configured
  const hasYearlyCycle = (cyclesList || []).some(
    item => item.targetFrequency === 'YEARLY' || item.monthsCount === 12
  );



  // Auto-Select Week based on Selected Start / End Date in Weekly Target mode
  useEffect(() => {
    if (targetFrequency === 'WEEKLY' && weeklyStartDate && weeklyEndDate) {
      setSelectedCalendarWeek(`${weeklyStartDate}|${weeklyEndDate}`);
    }
  }, [weeklyStartDate, weeklyEndDate, targetFrequency]);

  // Populate independent target maps for the CURRENT ACTIVE CYCLE from fetched cycles list
  useEffect(() => {
    if (cyclesList && cyclesList.length > 0) {
      const wMap: Record<string, number> = {};
      const mMap: Record<string, number> = {};
      const yMap: Record<string, number> = {};

      for (const cName of committeeList) {
        wMap[cName] = 0;
        mMap[cName] = 0;
        yMap[cName] = 0;
      }

      for (const item of cyclesList) {
        if (!item.committeeName) continue;
        const itemFreq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
        const val = item.yearlyTarget || item.monthlySum || 0;

        if (itemFreq === 'WEEKLY') {
          if (item.weeklyStartDate === weeklyStartDate && item.weeklyEndDate === weeklyEndDate) {
            wMap[item.committeeName] = val;
          }
        } else if (itemFreq === 'MONTHLY') {
          if (item.startMonth === selectedStartMonth && item.startYear === selectedStartYear) {
            mMap[item.committeeName] = val;
          }
        } else if (itemFreq === 'YEARLY') {
          // Yearly map uses locked Excel master cycle only (ignore month-shell span drift)
          const matchesMaster =
            Number(item.year) === masterStartYear ||
            (item.startMonth === masterStartMonth && item.startYear === masterStartYear);
          if (matchesMaster) {
            const valOnly = Number(item.yearlyTarget) || 0;
            if (valOnly > 0) {
              yMap[resolveCommitteeKey(item.committeeName, committeeList)] = valOnly;
            }
          }
        }
      }

      // Keep in-progress edits while planner is open (refetch must not wipe typed values).
      // Same guard for WEEKLY / MONTHLY / YEARLY — only the active frequency map is protected.
      if (!(viewMode === 'TARGET_PLANNER' && targetFrequency === 'WEEKLY')) {
        setWeeklyCommitteeTargets(wMap);
      }
      if (!(viewMode === 'TARGET_PLANNER' && targetFrequency === 'MONTHLY')) {
        setMonthlyCommitteeTargets(mMap);
      }
      if (!(viewMode === 'TARGET_PLANNER' && targetFrequency === 'YEARLY')) {
        setYearlyCommitteeTargets(yMap);
      }
    }
  }, [cyclesList, targetFrequency, weeklyStartDate, weeklyEndDate, selectedStartMonth, selectedStartYear, masterStartMonth, masterStartYear, viewMode, committeeList]);

  // Extract list of already configured committee names
  const configuredCommitteeNames = (cyclesList || []).map(c => c.committeeName);

  // 3. Fetch Detailed Target Config for selected Committee (Active when viewMode === 'TARGET_PLANNER')
  const { data: configData, isLoading: isFetching, refetch: refetchConfig } = useQuery<TargetConfigResponse>({
    queryKey: ['dom680TargetConfig', selectedCommittee, selectedStartYear, selectedStartMonth, selectedEndYear, selectedEndMonth],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/dom680-target-config', {
        params: {
          committeeName: selectedCommittee,
          startYear: selectedStartYear,
          startMonth: selectedStartMonth,
          endYear: selectedEndYear,
          endMonth: selectedEndMonth,
        },
      });
      return res.data.data;
    },
    enabled: !!selectedCommittee && viewMode === 'TARGET_PLANNER',
  });

  // Sync monthlyTargets state with generated sequence & fetched targets
  useEffect(() => {
    if (viewMode !== 'TARGET_PLANNER') return;

    const expectedCycle = generateCycleMonths(
      selectedStartMonth,
      selectedStartYear,
      selectedEndMonth,
      selectedEndYear
    );

    const existingMap = new Map<string, MonthlyTargetItem>();
    if (configData?.monthlyTargets) {
      for (const item of configData.monthlyTargets) {
        const itemY = item.year || selectedStartYear;
        existingMap.set(`${itemY}-${item.month}`, item);
      }
    }

    const merged = expectedCycle.map(cm => {
      const existing = existingMap.get(`${cm.year}-${cm.month}`);
      const calendarWeeks = generateCalendarWeeksForMonth(cm.year, cm.month);

      if (existing) {
        let mergedWeekly = existing.weeklyTargets || [];
        if (mergedWeekly.length === 0) {
          mergedWeekly = calendarWeeks.map(w => ({
            weekLabel: w.weekLabel,
            startDate: w.startDate,
            endDate: w.endDate,
            targetAmount: 0,
          }));
        }
        return {
          month: cm.month,
          year: cm.year,
          targetAmount: existing.targetAmount,
          weeklyTargets: mergedWeekly,
        };
      }
      return {
        month: cm.month,
        year: cm.year,
        targetAmount: 0,
        weeklyTargets: calendarWeeks.map(w => ({
          weekLabel: w.weekLabel,
          startDate: w.startDate,
          endDate: w.endDate,
          targetAmount: 0,
        })),
      };
    });

    if (selectedCommittee && configData) {
      setYearlyTarget(configData.yearlyTarget || 0);
      if (configData.targetFrequency) {
        setTargetFrequency(configData.targetFrequency);
      }
      setMonthlyTargets(merged);
    }
    setValidationError(null);
    setSuccessMsg(null);
  }, [configData, selectedStartMonth, selectedStartYear, selectedEndMonth, selectedEndYear, viewMode, selectedCommittee]);

  // Calculate Target Cycle Text
  const getCycleRangeText = () => {
    if (targetFrequency === 'WEEKLY') {
      const matched = EXCEL_WEEK_SCHEDULE.find(w => w.startDate === weeklyStartDate && w.endDate === weeklyEndDate);
      return matched ? matched.formattedLabel : formatWeekDisplayLabel(weeklyStartDate, weeklyEndDate);
    }
    if (targetFrequency === 'YEARLY') {
      const startMName = MONTH_SHORT[selectedStartMonth - 1] || 'Apr';
      const endMName = MONTH_SHORT[selectedEndMonth - 1] || 'Mar';
      return `Yearly Cycle: ${startMName} ${selectedStartYear} — ${endMName} ${selectedEndYear}`;
    }
    return formatMonthDisplayLabel(selectedStartYear, selectedStartMonth);
  };

  // Open Screen 2 to Edit Targets for a cycle item from Table
  const handleOpenTargetPlanner = (item?: TargetCycleListItem) => {
    setValidationError(null);
    if (item) {
      setSelectedCommittee(item.committeeName);
      setSelectedStartMonth(item.startMonth);
      setSelectedStartYear(item.startYear);
      setSelectedEndMonth(item.endMonth);
      setSelectedEndYear(item.endYear);
      setYearlyTarget(item.yearlyTarget);
      const freq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
      setTargetFrequency(freq);

      const val = item.yearlyTarget || item.monthlySum || 0;
      if (freq === 'WEEKLY') {
        setWeeklyCommitteeTargets(
          buildCommitteeTargetsMap(committeeList, [{ committeeName: item.committeeName, yearlyTarget: val }])
        );
      } else if (freq === 'YEARLY') {
        setYearlyCommitteeTargets(prev => ({ ...prev, [item.committeeName]: val }));
      } else {
        setMonthlyCommitteeTargets(
          buildCommitteeTargetsMap(committeeList, [{ committeeName: item.committeeName, yearlyTarget: val }])
        );
      }
    }
    setViewMode('TARGET_PLANNER');
  };

  // Proceed to Target Planner for New Cycle Creation (Shows ALL Committees)
  const handleCreateCycleHeader = async () => {
    setValidationError(null);

    if (targetFrequency === 'WEEKLY') {
      const isAlreadyConfigured = (cyclesList || []).some(c => {
        if (c.targetFrequency !== 'WEEKLY') return false;
        if (c.weeklyStartDate && c.weeklyEndDate) {
          const cStart = c.weeklyStartDate.substring(0, 10);
          const cEnd = c.weeklyEndDate.substring(0, 10);
          const selStart = weeklyStartDate.substring(0, 10);
          const selEnd = weeklyEndDate.substring(0, 10);
          return selStart <= cEnd && selEnd >= cStart;
        }
        return false;
      });

      if (isAlreadyConfigured) {
        setValidationError(`⚠️ Date range ${weeklyStartDate} to ${weeklyEndDate} is already configured for another week.`);
        return;
      }
    }

    // Force network APIs before opening planner (visible in Network tab)
    const fresh = await refreshTargetPlannerApis();

    setSelectedCommittee('');

    if (targetFrequency === 'YEARLY') {
      setSelectedStartMonth(masterStartMonth);
      setSelectedStartYear(masterStartYear);
      setSelectedEndMonth(masterEndMonth);
      setSelectedEndYear(masterEndYear);
      setYearlyCommitteeTargets(
        buildYearlyTargetsFromList(fresh, committeeList, masterStartMonth, masterStartYear)
      );
    } else {
      // Existing week/month create flow: blank maps for selected period
      setWeeklyCommitteeTargets({});
      setMonthlyCommitteeTargets({});
      setYearlyCommitteeTargets({});
    }

    setViewMode('TARGET_PLANNER');
  };

  // Helper to trigger Next Cycle
  const handleSetNextCycle = () => {
    let nextStartMonth = selectedEndMonth + 1;
    let nextStartYear = selectedEndYear;
    if (nextStartMonth > 12) {
      nextStartMonth = 1;
      nextStartYear++;
    }
    let nextEndMonth = nextStartMonth === 1 ? 12 : nextStartMonth - 1;
    let nextEndYear = nextStartMonth === 1 ? nextStartYear : nextStartYear + 1;

    setSelectedStartMonth(nextStartMonth);
    setSelectedStartYear(nextStartYear);
    setSelectedEndMonth(nextEndMonth);
    setSelectedEndYear(nextEndYear);
  };

  const handleStartMonthChange = (m: number) => {
    setSelectedStartMonth(m);
  };

  const handleStartYearChange = (y: number) => {
    setSelectedStartYear(y);
  };

  // Committee Target Amount Change Handler
  const handleCommitteeTargetChange = (cName: string, amount: number) => {
    if (targetFrequency === 'WEEKLY') {
      setWeeklyCommitteeTargets(prev => ({ ...prev, [cName]: amount }));
    } else if (targetFrequency === 'YEARLY') {
      setYearlyCommitteeTargets(prev => ({ ...prev, [cName]: amount }));
    } else {
      setMonthlyCommitteeTargets(prev => ({ ...prev, [cName]: amount }));
    }
  };

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/admin/reports/dom680-target-config/${id}`);
      return res.data;
    },
    onSuccess: () => {
      setSuccessMsg('Target cycle deleted successfully!');
      setDeleteConfirmId(null);
      refetchCyclesList();
      queryClient.invalidateQueries({ queryKey: ['dom680TrackerReport'] });
      queryClient.invalidateQueries({ queryKey: ['dom680TargetConfig'] });
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      setValidationError(err.response?.data?.error || err.message || 'Failed to delete target cycle');
    },
  });

  // Save Mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post('/admin/reports/dom680-target-config', payload);
      return res.data;
    },
    onSuccess: () => {
      setSuccessMsg('Target configuration saved successfully!');
      setValidationError(null);
      setRegistryTab(targetFrequency);
      refetchCyclesList();
      queryClient.invalidateQueries({ queryKey: ['dom680TrackerReport'] });
      queryClient.invalidateQueries({ queryKey: ['dom680TargetConfig'] });
      setTimeout(() => {
        setSuccessMsg(null);
      }, 4000);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || err.message || 'Failed to save targets';
      setValidationError(msg);
      setSuccessMsg(null);
    },
  });

  // Monthly sum calculation for Screen 2
  const totalMonthlySum = monthlyTargets.reduce((sum, m) => sum + (Number(m.targetAmount) || 0), 0);
  const totalCommitteeSum = committeeList.reduce((sum, cName) => sum + (getCurrentCommitteeTarget(cName) || 0), 0);

  const handleMonthTargetChange = (monthNum: number, yearNum: number, value: number) => {
    setMonthlyTargets(prev =>
      prev.map(m => {
        const itemYear = m.year ?? yearNum;
        if (m.month === monthNum && itemYear === yearNum) {
          return { ...m, month: monthNum, year: yearNum, targetAmount: value };
        }
        return m;
      })
    );
  };

  const handleSavePlanner = async (targetOverrides?: Record<string, number>) => {
    setValidationError(null);
    setSuccessMsg(null);

    try {
      // Save targets for active committee selection or all committees
      const activeCommittees = selectedCommittee ? [selectedCommittee] : committeeList;

      for (const cName of activeCommittees) {
        let cTarget = getCurrentCommitteeTarget(cName);
        if (targetOverrides && cName in targetOverrides) {
          cTarget = targetOverrides[cName];
        }

        // Do not overwrite DB with silent UI zeros (Clear still passes overrides).
        // Only committees with a non-zero amount (or explicit Clear override) are saved.
        if (
          !(targetOverrides && cName in targetOverrides) &&
          (!cTarget || Number(cTarget) === 0)
        ) {
          continue;
        }

        let calcYearlyTarget = cTarget;
        let defaultMonthlyVal = 0;
        let defaultWeeklyVal = 0;
        const monthsCount = monthlyTargets.length || 12;

        const sumMonthly = monthlyTargets.reduce((acc, m) => acc + (Number(m.targetAmount) || 0), 0);
        const sumWeekly = monthlyTargets.reduce((acc, m) => {
          return acc + (m.weeklyTargets || []).reduce((wAcc, w) => wAcc + (Number(w.targetAmount) || 0), 0);
        }, 0);

        if (cTarget > 0) {
          if (targetFrequency === 'MONTHLY') {
            defaultMonthlyVal = cTarget;
            calcYearlyTarget = cTarget;
            defaultWeeklyVal = Math.round(cTarget / 4);
          } else if (targetFrequency === 'WEEKLY') {
            defaultWeeklyVal = cTarget;
            defaultMonthlyVal = cTarget;
            calcYearlyTarget = cTarget;
          } else if (targetFrequency === 'YEARLY') {
            calcYearlyTarget = cTarget;
            defaultMonthlyVal = Math.round(cTarget / monthsCount);
            defaultWeeklyVal = Math.round(defaultMonthlyVal / 4);
          }
        } else {
          calcYearlyTarget = sumMonthly > 0 ? sumMonthly : sumWeekly;
        }

        const wStartParts = weeklyStartDate.split('-');
        const wEndParts = weeklyEndDate.split('-');
        const wStartM = parseInt(wStartParts[1] || String(selectedStartMonth), 10);
        const wStartY = parseInt(wStartParts[0] || String(selectedStartYear), 10);
        const wEndM = parseInt(wEndParts[1] || String(selectedEndMonth), 10);
        const wEndY = parseInt(wEndParts[0] || String(selectedEndYear), 10);

        const activeCycleMonths = targetFrequency === 'WEEKLY'
          ? generateCycleMonths(wStartM, wStartY, wEndM, wEndY)
          : generateCycleMonths(selectedStartMonth, selectedStartYear, selectedEndMonth, selectedEndYear);

        const payload = {
          committeeName: cName,
          year: targetFrequency === 'WEEKLY' ? wStartY : selectedStartYear,
          startMonth: targetFrequency === 'WEEKLY' ? wStartM : selectedStartMonth,
          startYear: targetFrequency === 'WEEKLY' ? wStartY : selectedStartYear,
          endMonth: targetFrequency === 'WEEKLY' ? wEndM : selectedEndMonth,
          endYear: targetFrequency === 'WEEKLY' ? wEndY : selectedEndYear,
          yearlyTarget: targetFrequency === 'YEARLY' ? cTarget : 0,
          targetFrequency: targetFrequency,
          monthlyTargets: activeCycleMonths.map(cm => {
            const existing = monthlyTargets.find(m => m.month === cm.month && ((m.year ?? cm.year) === cm.year));
            const userEnteredMVal = existing ? Number(existing.targetAmount) || 0 : 0;

            let weeksList = existing?.weeklyTargets || [];

            if (targetFrequency === 'WEEKLY') {
              const startWDate = weeklyStartDate;
              const endWDate = weeklyEndDate;

              const cmStartStr = `${cm.year}-${String(cm.month).padStart(2, '0')}-01`;
              const lastD = new Date(cm.year, cm.month, 0).getDate();
              const cmEndStr = `${cm.year}-${String(cm.month).padStart(2, '0')}-${String(lastD).padStart(2, '0')}`;

              const isOverlap = startWDate <= cmEndStr && endWDate >= cmStartStr;

              if (isOverlap) {
                const matchedExcelWeek = EXCEL_WEEK_SCHEDULE.find(w => w.startDate === startWDate && w.endDate === endWDate);
                const computedWeekLabel = matchedExcelWeek ? matchedExcelWeek.label : `Week 1`;

                weeksList = [{
                  weekLabel: computedWeekLabel,
                  startDate: startWDate,
                  endDate: endWDate,
                  targetAmount: cTarget,
                }];
              } else {
                weeksList = [];
              }
            } else if (weeksList.length === 0 && targetFrequency === 'MONTHLY') {
              weeksList = generateCalendarWeeksForMonth(cm.year, cm.month).map(w => ({
                weekLabel: w.weekLabel,
                startDate: w.startDate,
                endDate: w.endDate,
                targetAmount: 0,
              }));
            }

            const mVal = targetFrequency === 'MONTHLY' ? cTarget : 0;

            return {
              month: cm.month,
              year: cm.year,
              targetAmount: mVal,
              weeklyTargets: weeksList.map(w => ({
                id: w.id,
                weekLabel: w.weekLabel || 'Week 1',
                startDate: w.startDate,
                endDate: w.endDate,
                targetAmount: Number(w.targetAmount) || 0,
              })),
            };
          }),
        };
        await saveMutation.mutateAsync(payload);
      }

      setSuccessMsg('All Committee Target Configurations saved successfully!');
    } catch (err: any) {
      setValidationError(err.message || 'Failed to save committee targets');
    }
  };

  // Filter cycles list according to active registryTab
  const filteredCyclesList = (cyclesList || []).filter(item => {
    const itemFreq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
    return itemFreq === registryTab;
  });

  const groupedCyclesList = useMemo(() => {
    const map = new Map<string, {
      key: string;
      startMonth: number;
      startYear: number;
      endMonth: number;
      endYear: number;
      monthsCount: number;
      targetFrequency: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
      sampleItem: TargetCycleListItem;
      totalTargetSum: number;
      items: TargetCycleListItem[];
    }>();

    for (const item of filteredCyclesList) {
      const itemFreq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
      // YEARLY: one registry row for locked Excel master cycle (prevents duplicate Sep–May rows)
      const cycleKey =
        itemFreq === 'YEARLY'
          ? `YEARLY_${masterStartYear}_${masterStartMonth}_${masterEndYear}_${masterEndMonth}`
          : itemFreq === 'WEEKLY' && item.weeklyStartDate
            ? `WEEKLY_${item.weeklyStartDate}_${item.weeklyEndDate}`
            : `${itemFreq}_${item.startYear}_${item.startMonth}_${item.endYear}_${item.endMonth}`;

      if (!map.has(cycleKey)) {
        map.set(cycleKey, {
          key: cycleKey,
          startMonth: itemFreq === 'YEARLY' ? masterStartMonth : item.startMonth,
          startYear: itemFreq === 'YEARLY' ? masterStartYear : item.startYear,
          endMonth: itemFreq === 'YEARLY' ? masterEndMonth : item.endMonth,
          endYear: itemFreq === 'YEARLY' ? masterEndYear : item.endYear,
          monthsCount:
            itemFreq === 'YEARLY'
              ? generateCycleMonths(masterStartMonth, masterStartYear, masterEndMonth, masterEndYear).length
              : item.monthsCount,
          targetFrequency: itemFreq as any,
          sampleItem: item,
          totalTargetSum: item.yearlyTarget || item.monthlySum || 0,
          items: [item],
        });
      } else {
        const existing = map.get(cycleKey)!;
        existing.totalTargetSum += (item.yearlyTarget || item.monthlySum || 0);
        existing.items.push(item);
      }
    }

    return Array.from(map.values());
  }, [filteredCyclesList, masterStartMonth, masterStartYear, masterEndMonth, masterEndYear]);

  const getUniqueGroupedCount = (freq: 'WEEKLY' | 'MONTHLY' | 'YEARLY') => {
    const filtered = (cyclesList || []).filter(c => {
      const itemFreq = c.targetFrequency || (c.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
      return itemFreq === freq;
    });

    const set = new Set<string>();
    for (const item of filtered) {
      const itemFreq = item.targetFrequency || (item.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
      const key =
        itemFreq === 'YEARLY'
          ? `YEARLY_${masterStartYear}_${masterStartMonth}_${masterEndYear}_${masterEndMonth}`
          : itemFreq === 'WEEKLY' && item.weeklyStartDate
            ? `WEEKLY_${item.weeklyStartDate}_${item.weeklyEndDate}`
            : `${itemFreq}_${item.startYear}_${item.startMonth}_${item.endYear}_${item.endMonth}`;
      set.add(key);
    }
    return set.size;
  };

  const weeklyCyclesCount = useMemo(() => getUniqueGroupedCount('WEEKLY'), [cyclesList]);
  const monthlyCyclesCount = useMemo(() => getUniqueGroupedCount('MONTHLY'), [cyclesList]);
  const yearlyCyclesCount = useMemo(
    () => getUniqueGroupedCount('YEARLY'),
    [cyclesList, masterStartMonth, masterStartYear, masterEndMonth, masterEndYear]
  );

  return (
    <Layout title="DOM 680 TARGET PLANNER">
      <div className="space-y-5 max-w-6xl mx-auto pb-16 font-sans">
        {/* COMPACT TOP NAVIGATION HEADER */}
        <div className="bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-md border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin/reports/dom680')}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-all cursor-pointer shadow-sm active:scale-95 shrink-0"
              title="Back to DOM 680 Report"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <span className="p-1 bg-emerald-500/20 text-emerald-400 rounded-lg border border-emerald-500/30">
                  <Target className="w-4 h-4" />
                </span>
                <h1 className="text-base font-black tracking-tight text-white">DOM 680 Target Hub</h1>
              </div>
              <p className="text-[11px] text-slate-400 font-medium">
                Configure committee-wise target cycles & allocate weekly, monthly, or yearly targets
              </p>
            </div>
          </div>

          {/* Segmented View Switcher Pill */}
          <div className="bg-slate-950 p-1 rounded-xl border border-slate-800 flex items-center space-x-1 shadow-inner self-start md:self-auto">
            <button
              onClick={() => setViewMode('REGISTRY')}
              className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all flex items-center space-x-1.5 cursor-pointer ${viewMode === 'REGISTRY'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
            >
              <ListFilter className="w-3.5 h-3.5" />
              <span>📋 Cycles Registry</span>
            </button>

            <button
              onClick={async () => {
                await refreshTargetPlannerApis();
                setViewMode('TARGET_PLANNER');
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all flex items-center space-x-1.5 cursor-pointer ${viewMode === 'TARGET_PLANNER'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
            >
              <Target className="w-3.5 h-3.5" />
              <span>🎯 Target Planner</span>
            </button>
          </div>
        </div>

        {/* ALERT BANNERS */}


        {validationError && (
          <div className="p-3 px-4 bg-rose-50 border border-rose-200 text-rose-900 text-xs font-bold rounded-xl flex items-center space-x-2.5 shadow-sm animate-in fade-in">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-3 px-4 bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-bold rounded-xl flex items-center space-x-2.5 shadow-sm animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* ========================================================================================= */}
        {/* SCREEN 1: TARGET CYCLES REGISTRY & ADD CYCLE FORM                                         */}
        {/* ========================================================================================= */}
        {viewMode === 'REGISTRY' && (
          <div className="space-y-5 animate-in fade-in duration-200">
            {/* SCREEN 1 COMPACT FORM: CREATE TARGET CYCLE */}
            <div className="bg-white p-4 px-5 rounded-2xl border border-slate-200 shadow-sm space-y-3.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-2.5 gap-2">
                <div>
                  <h2 className="text-xs font-black text-slate-800 flex items-center space-x-1.5 uppercase tracking-wider">
                    <Plus className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Create Target Cycle</span>
                  </h2>
                  <p className="text-[11px] text-slate-500 font-medium mt-0.5">Select target frequency and custom start/end period</p>
                </div>
                {/* Master Yearly Cycle — locked to Excel sheet (display only) */}
                <div
                  className="bg-blue-50 text-blue-900 text-[11px] font-extrabold px-3 py-1.5 rounded-xl border border-blue-300 flex items-center space-x-1.5 shadow-sm self-start sm:self-auto"
                  title="Locked to Excel sheet yearly range"
                >
                  <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span>📅 Yearly Cycle: {MONTH_SHORT[masterStartMonth - 1]} {masterStartYear} — {MONTH_SHORT[masterEndMonth - 1]} {masterEndYear}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3.5 items-end">
                {/* 1. Target Frequency (Custom Single-Select Popover UI matching Admin Portal) */}
                <div ref={freqSelectRef} className="relative">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">1. Target Frequency</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsFreqSelectOpen(!isFreqSelectOpen)}
                      className={`w-full bg-white border rounded-xl pl-3.5 pr-8 h-9.5 flex items-center justify-between text-xs font-extrabold cursor-pointer shadow-sm transition-all ${
                        isFreqSelectOpen
                          ? 'border-slate-400 ring-2 ring-slate-200 text-slate-900'
                          : 'border-slate-300 text-slate-800 hover:border-slate-400'
                      }`}
                    >
                      <div className="flex items-center space-x-2 truncate">
                        <Filter className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="truncate">
                          {targetFrequency === 'YEARLY'
                            ? 'Yearly Target'
                            : targetFrequency === 'WEEKLY'
                            ? 'Weekly Target'
                            : 'Monthly Target'}
                        </span>
                      </div>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 stroke-[2.5]" />
                    </button>

                    {/* Frequency Options Popover Modal */}
                    {isFreqSelectOpen && (
                      <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl w-60 p-2 animate-in fade-in zoom-in-95 space-y-1">
                        {[
                          { label: 'Yearly Target', value: 'YEARLY' },
                          { label: 'Monthly Target', value: 'MONTHLY' },
                          { label: 'Weekly Target', value: 'WEEKLY' },
                        ].map(opt => {
                          const isSelected = targetFrequency === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={() => {
                                setTargetFrequency(opt.value as any);
                                setIsFreqSelectOpen(false);
                                setValidationError(null);
                                // Yearly only: period UI follows Excel master yearly cycle
                                if (opt.value === 'YEARLY') {
                                  setSelectedStartMonth(masterStartMonth);
                                  setSelectedStartYear(masterStartYear);
                                  setSelectedEndMonth(masterEndMonth);
                                  setSelectedEndYear(masterEndYear);
                                }
                              }}
                              className={`flex items-center space-x-3 px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-slate-100 text-slate-950 font-black border border-slate-200'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                isSelected ? 'border-slate-800 bg-slate-800' : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
                              </div>
                              <span className="truncate">{opt.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* 2 & 3. Period Range Inputs (Dynamic based on Frequency) */}
                {targetFrequency === 'WEEKLY' ? (
                  <>
                    {/* 2. Select Calendar Week (Custom Single-Select Popover UI matching Admin Portal) */}
                    <div ref={weekSelectRef} className="md:col-span-2 max-w-[440px] relative">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">2. Select Calendar Week</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsWeekSelectOpen(!isWeekSelectOpen)}
                          className={`w-full bg-white border rounded-xl pl-3.5 pr-8 h-9.5 flex items-center justify-between text-xs font-extrabold cursor-pointer shadow-sm transition-all ${
                            isWeekSelectOpen
                              ? 'border-slate-400 ring-2 ring-slate-200 text-slate-900'
                              : 'border-slate-300 text-slate-800 hover:border-slate-400'
                          }`}
                        >
                          <div className="flex items-center space-x-2 truncate">
                            <Filter className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="truncate">
                              {(() => {
                                const matched = EXCEL_WEEK_SCHEDULE.find(w => w.startDate === weeklyStartDate && w.endDate === weeklyEndDate);
                                if (matched) {
                                  return matched.formattedLabel;
                                }
                                if (weeklyStartDate && weeklyEndDate) {
                                  return formatWeekDisplayLabel(weeklyStartDate, weeklyEndDate);
                                }
                                return 'Choose Calendar Week...';
                              })()}
                            </span>
                          </div>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 stroke-[2.5]" />
                        </button>

                        {/* Custom Single-Select Popover Dropdown Modal */}
                        {isWeekSelectOpen && (
                          <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl w-full sm:w-96 p-3 animate-in fade-in zoom-in-95 space-y-2.5">
                            {/* Search Input Box (Matching Admin Portal Filter UI) */}
                            <div className="relative">
                              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                              <input
                                type="text"
                                placeholder="Search weeks by label or date..."
                                value={weekSearchQuery}
                                onChange={e => setWeekSearchQuery(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 font-medium"
                                autoFocus
                              />
                            </div>

                            {/* All 36 Excel Schedule Weeks List (Single Select) */}
                            <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                              {(() => {
                                const query = weekSearchQuery.trim().toLowerCase();
                                const filtered = EXCEL_WEEK_SCHEDULE.filter(w => {
                                  if (!query) return true;
                                  return (
                                    w.label.toLowerCase().includes(query) ||
                                    w.formattedLabel.toLowerCase().includes(query) ||
                                    w.startDate.includes(query) ||
                                    w.endDate.includes(query) ||
                                    w.displayStart.includes(query) ||
                                    w.displayEnd.includes(query)
                                  );
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="p-3 text-center text-xs text-slate-400 font-medium">
                                      No matching weeks found
                                    </div>
                                  );
                                }

                                return filtered.map(w => {
                                  const isSelected = weeklyStartDate === w.startDate && weeklyEndDate === w.endDate;

                                  const isAlreadyConfigured = (cyclesList || []).some(c => {
                                    if (c.targetFrequency !== 'WEEKLY') return false;
                                    if (c.weeklyStartDate && c.weeklyEndDate) {
                                      const cStart = c.weeklyStartDate.substring(0, 10);
                                      const cEnd = c.weeklyEndDate.substring(0, 10);
                                      return cStart === w.startDate && cEnd === w.endDate;
                                    }
                                    return false;
                                  });

                                  const isDisabled = !isSelected && isAlreadyConfigured;

                                  return (
                                    <div
                                      key={w.label}
                                      onClick={() => {
                                        if (isDisabled) return;
                                        setWeeklyStartDate(w.startDate);
                                        setWeeklyEndDate(w.endDate);
                                        setSelectedCalendarWeek(`${w.startDate}|${w.endDate}`);
                                        setIsWeekSelectOpen(false);
                                        setValidationError(null);
                                      }}
                                      className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs transition-all ${
                                        isDisabled
                                          ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400'
                                          : isSelected
                                          ? 'bg-slate-100 text-slate-950 font-black border border-slate-200 cursor-pointer'
                                          : 'hover:bg-slate-50 text-slate-700 font-bold cursor-pointer'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3 truncate">
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                          isSelected ? 'border-slate-800 bg-slate-800' : 'border-slate-300'
                                        }`}>
                                          {isSelected && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
                                        </div>
                                        <span className="truncate">
                                          {w.formattedLabel}
                                        </span>
                                      </div>
                                      {isDisabled && (
                                        <span className="text-[10px] font-extrabold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0 ml-2">
                                          Already Configured
                                        </span>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : targetFrequency === 'YEARLY' ? (
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">2. Target Period (Master Cycle)</label>
                    <div className="bg-slate-50 border border-slate-200 text-slate-900 font-extrabold text-xs rounded-xl px-3.5 h-9.5 flex items-center justify-between shadow-sm">
                      <span className="flex items-center space-x-1.5">
                        <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span>📅 Master Yearly Cycle: {MONTH_SHORT[masterStartMonth - 1]} {masterStartYear} — {MONTH_SHORT[masterEndMonth - 1]} {masterEndYear}</span>
                      </span>
                      <span className="text-[10px] font-black text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md">
                        {generateCycleMonths(masterStartMonth, masterStartYear, masterEndMonth, masterEndYear).length} Months
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* 2. Select Month (Custom Single-Select Popover UI matching Admin Portal) */}
                    <div ref={monthSelectRef} className="md:col-span-2 relative">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">2. Select Month</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsMonthSelectOpen(!isMonthSelectOpen)}
                          className={`w-full bg-white border rounded-xl pl-3.5 pr-8 h-9.5 flex items-center justify-between text-xs font-extrabold cursor-pointer shadow-sm transition-all ${
                            isMonthSelectOpen
                              ? 'border-slate-400 ring-2 ring-slate-200 text-slate-900'
                              : 'border-slate-300 text-slate-800 hover:border-slate-400'
                          }`}
                        >
                          <div className="flex items-center space-x-2 truncate">
                            <Filter className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="truncate">
                              {selectedStartMonth && selectedStartYear
                                ? formatMonthDisplayLabel(selectedStartYear, selectedStartMonth)
                                : 'Choose Month...'}
                            </span>
                          </div>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 stroke-[2.5]" />
                        </button>

                        {/* Custom Single-Select Popover Dropdown Modal */}
                        {isMonthSelectOpen && (
                          <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl w-full sm:w-96 p-3 animate-in fade-in zoom-in-95 space-y-2.5">
                            {/* Search Input Box */}
                            <div className="relative">
                              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                              <input
                                type="text"
                                placeholder="Search months..."
                                value={monthSearchQuery}
                                onChange={e => setMonthSearchQuery(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 font-medium"
                                autoFocus
                              />
                            </div>

                            {/* Months from Excel sheet only (within master yearly cycle) */}
                            <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                              {(() => {
                                const cycleMonths = generateCycleMonths(
                                  masterStartMonth,
                                  masterStartYear,
                                  masterEndMonth,
                                  masterEndYear
                                );
                                const cycleKeySet = new Set(
                                  cycleMonths.map((cm) => `${cm.year}-${cm.month}`)
                                );
                                // Only months defined in the Excel operational sheet
                                const allMonthsInCycle = EXCEL_OPERATIONAL_MONTHS.filter((em) =>
                                  cycleKeySet.has(`${em.year}-${em.month}`)
                                ).map((em) => ({ month: em.month, year: em.year }));

                                const query = monthSearchQuery.trim().toLowerCase();
                                const filtered = allMonthsInCycle.filter(cm => {
                                  if (!query) return true;
                                  const mName = formatMonthDisplayLabel(cm.year, cm.month).toLowerCase();
                                  return mName.includes(query);
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="p-3 text-center text-xs text-slate-400 font-medium">
                                      No matching months found
                                    </div>
                                  );
                                }

                                return filtered.map(cm => {
                                  const mName = formatMonthDisplayLabel(cm.year, cm.month);
                                  const isSelected = selectedStartMonth === cm.month && selectedStartYear === cm.year;

                                  const isAlreadyConfigured = (cyclesList || []).some(c => {
                                    if (c.targetFrequency !== 'MONTHLY') return false;
                                    return c.startMonth === cm.month && c.startYear === cm.year;
                                  });

                                  const isDisabled = !isSelected && isAlreadyConfigured;

                                  return (
                                    <div
                                      key={`${cm.year}-${cm.month}`}
                                      onClick={() => {
                                        if (isDisabled) return;
                                        setSelectedStartMonth(cm.month);
                                        setSelectedStartYear(cm.year);
                                        setSelectedEndMonth(cm.month);
                                        setSelectedEndYear(cm.year);
                                        setMonthlyStartDate(formatInitialMonthDate(cm.month, cm.year, false));
                                        setMonthlyEndDate(formatInitialMonthDate(cm.month, cm.year, true));
                                        setIsMonthSelectOpen(false);
                                        setValidationError(null);
                                      }}
                                      className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs transition-all ${
                                        isDisabled
                                          ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400'
                                          : isSelected
                                          ? 'bg-slate-100 text-slate-950 font-black border border-slate-200 cursor-pointer'
                                          : 'hover:bg-slate-50 text-slate-700 font-bold cursor-pointer'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3 truncate">
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                          isSelected ? 'border-slate-800 bg-slate-800' : 'border-slate-300'
                                        }`}>
                                          {isSelected && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
                                        </div>
                                        <span className="truncate">
                                          {mName}
                                        </span>
                                      </div>
                                      {isDisabled && (
                                        <span className="text-[10px] font-extrabold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0 ml-2">
                                          Already Configured
                                        </span>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* 4. Plan & Configure Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleCreateCycleHeader}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-black h-9.5 px-3.5 rounded-xl text-xs shadow-sm transition-all flex items-center justify-center space-x-1.5 cursor-pointer active:scale-95"
                  >
                    <span>Proceed to Set Targets</span>
                    <ArrowRight className="w-3.5 h-3.5 text-emerald-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* SCREEN 1 TABLE: CONFIGURED TARGET CYCLES LIST */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-base font-black text-slate-900 flex items-center space-x-2">
                    <ListFilter className="w-4 h-4 text-emerald-600" />
                    <span>Saved Target Cycles Registry</span>
                  </h2>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">Click "Set / Edit Targets" on any cycle to configure detailed target values per committee</p>
                </div>

                {/* REGISTRY TABS: WEEKLY, MONTHLY, YEARLY */}
                <div className="flex items-center space-x-1.5 bg-slate-100 p-1 rounded-2xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setRegistryTab('WEEKLY')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center space-x-1.5 cursor-pointer ${registryTab === 'WEEKLY'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                      }`}
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Weekly</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${registryTab === 'WEEKLY' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                      {weeklyCyclesCount}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRegistryTab('MONTHLY')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center space-x-1.5 cursor-pointer ${registryTab === 'MONTHLY'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                      }`}
                  >
                    <Building className="w-3.5 h-3.5" />
                    <span>Monthly</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${registryTab === 'MONTHLY' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                      {monthlyCyclesCount}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRegistryTab('YEARLY')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center space-x-1.5 cursor-pointer ${registryTab === 'YEARLY'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                      }`}
                  >
                    <Target className="w-3.5 h-3.5" />
                    <span>Yearly</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${registryTab === 'YEARLY' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                      {yearlyCyclesCount}
                    </span>
                  </button>
                </div>
              </div>

              {isCyclesLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mr-2" />
                  <span className="text-xs font-semibold text-slate-500">Loading saved target cycles...</span>
                </div>
              ) : !groupedCyclesList || groupedCyclesList.length === 0 ? (
                <div className="p-8 text-center bg-slate-50/80 rounded-2xl border border-dashed border-slate-300">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-2">
                    <Target className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-black text-slate-800">No {registryTab} Target Cycles Configured Yet</p>
                  <p className="text-[11px] text-slate-500 mt-1 max-w-sm mx-auto">Select target frequency and period above, then click "Proceed to Set Targets"!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-black uppercase tracking-wider border-b border-slate-200">
                        <th className="py-3.5 px-4 rounded-l-xl">Target Cycle Period</th>
                        <th className="py-3.5 px-4 rounded-r-xl text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                      {groupedCyclesList.map((group: any) => {
                        // YEARLY registry period follows Master Yearly Cycle (not April-biased month shells)
                        const isYearlyGroup = group.targetFrequency === 'YEARLY';
                        const displayStartMonth = isYearlyGroup ? masterStartMonth : group.startMonth;
                        const displayStartYear = isYearlyGroup ? masterStartYear : group.startYear;
                        const displayEndMonth = isYearlyGroup ? masterEndMonth : group.endMonth;
                        const displayEndYear = isYearlyGroup ? masterEndYear : group.endYear;
                        const displayMonthsCount = isYearlyGroup
                          ? generateCycleMonths(masterStartMonth, masterStartYear, masterEndMonth, masterEndYear).length
                          : group.monthsCount;

                        const startMName = MONTH_SHORT[displayStartMonth - 1] || 'Jan';
                        const endMName = MONTH_SHORT[displayEndMonth - 1] || 'Dec';
                        const itemWStart = group.sampleItem?.weeklyStartDate || weeklyStartDate;
                        const itemWEnd = group.sampleItem?.weeklyEndDate || weeklyEndDate;
                        const itemWLabel = group.sampleItem?.weekLabel || 'Week 1';
                        const rangeLabel = group.targetFrequency === 'WEEKLY'
                          ? formatWeekDisplayLabel(itemWStart, itemWEnd, itemWLabel)
                          : (displayStartMonth === displayEndMonth && displayStartYear === displayEndYear)
                            ? formatMonthDisplayLabel(displayStartYear, displayStartMonth)
                            : `${startMName} ${displayStartYear} — ${endMName} ${displayEndYear} (${displayMonthsCount} M)`;

                        return (
                          <tr key={group.key} className="hover:bg-slate-50/80 transition-colors group">
                            <td className="py-3.5 px-4 font-bold text-slate-700">
                              <span className="bg-slate-100 text-slate-900 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-black flex items-center space-x-1.5 w-fit shadow-sm">
                                <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                <span>{rangeLabel}</span>
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-center">
                              <div className="flex items-center justify-center space-x-2">
                                <button
                                  onClick={async () => {
                                    setValidationError(null);
                                    const fresh = await refreshTargetPlannerApis();

                                    setSelectedCommittee('');
                                    setSelectedStartMonth(displayStartMonth);
                                    setSelectedStartYear(displayStartYear);
                                    setSelectedEndMonth(displayEndMonth);
                                    setSelectedEndYear(displayEndYear);
                                    setTargetFrequency(group.targetFrequency);
                                    if (itemWStart) setWeeklyStartDate(itemWStart);
                                    if (itemWEnd) setWeeklyEndDate(itemWEnd);
                                    if (itemWStart && itemWEnd) setSelectedCalendarWeek(`${itemWStart}|${itemWEnd}`);

                                    if (group.targetFrequency === 'YEARLY') {
                                      setYearlyCommitteeTargets(
                                        buildYearlyTargetsFromList(
                                          fresh,
                                          committeeList,
                                          displayStartMonth,
                                          displayStartYear
                                        )
                                      );
                                    } else if (group.items && group.items.length > 0) {
                                      const targetsMap = buildCommitteeTargetsMap(committeeList, group.items);
                                      if (group.targetFrequency === 'WEEKLY') {
                                        setWeeklyCommitteeTargets(targetsMap);
                                      } else {
                                        setMonthlyCommitteeTargets(targetsMap);
                                      }
                                    } else {
                                      // No saved rows for this period — start clean zeros
                                      const emptyMap = buildCommitteeTargetsMap(committeeList, []);
                                      if (group.targetFrequency === 'WEEKLY') {
                                        setWeeklyCommitteeTargets(emptyMap);
                                      } else {
                                        setMonthlyCommitteeTargets(emptyMap);
                                      }
                                    }

                                    setViewMode('TARGET_PLANNER');
                                  }}
                                  className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm active:scale-95"
                                  title="Open Target Planner to set/edit detailed committee targets"
                                >
                                  <Target className="w-3.5 h-3.5 text-emerald-400" />
                                  <span>Set / Edit Targets</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================================= */}
        {/* SCREEN 2: TARGET ALLOCATION PLANNER (COMMITTEE BREAKDOWN LIST)                           */}
        {/* ========================================================================================= */}
        {viewMode === 'TARGET_PLANNER' && (
          <div className="space-y-6 animate-in fade-in duration-200">
            {/* Header Navigation Bar */}
            <div className="bg-white rounded-2xl p-4 px-5 border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setViewMode('REGISTRY')}
                  className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl transition-all cursor-pointer shadow-sm flex items-center space-x-1 active:scale-95"
                  title="Back to Target Cycles Registry"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="p-1 bg-emerald-100 text-emerald-800 rounded-md">
                      <Target className="w-4 h-4" />
                    </span>
                    <h1 className="text-base font-black text-slate-900 tracking-tight">
                      Target Planner: <span className="text-emerald-600">{targetFrequency} TARGET</span>
                    </h1>
                  </div>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Configure Committee Targets for 📅 {getCycleRangeText()}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('REGISTRY')}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-3 py-1.5 rounded-xl text-xs transition-all flex items-center space-x-1 cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to Registry</span>
                </button>

                {/* YEARLY: Clear/Delete disabled for now — uncomment to re-enable yearly clear */}
                {targetFrequency !== 'YEARLY' && (
                  <button
                    type="button"
                    onClick={() => setConfirmModalConfig({ isOpen: true, type: 'CLEAR_ALL' })}
                    className="bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold px-3 py-1.5 rounded-xl text-xs transition-all flex items-center space-x-1 cursor-pointer active:scale-95"
                    title="Clear all target inputs"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    <span>Clear All Targets</span>
                  </button>
                )}

                <button
                  onClick={() => handleSavePlanner()}
                  disabled={saveMutation.isPending}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-black px-4 py-2 rounded-xl text-xs shadow-md transition-all flex items-center space-x-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                  ) : (
                    <Save className="w-3.5 h-3.5 text-emerald-400" />
                  )}
                  <span>{saveMutation.isPending ? 'Saving...' : 'Save Configuration'}</span>
                </button>
              </div>
            </div>

            {selectedCommittee && (
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-fit">
                <span className="text-[11px] text-blue-900 font-semibold">Focusing Committee: <strong className="font-black text-blue-900">{selectedCommittee}</strong></span>
                <button
                  type="button"
                  onClick={() => setSelectedCommittee('')}
                  className="text-[10px] font-black bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded-md transition-all cursor-pointer"
                >
                  Show All Committees
                </button>
              </div>
            )}

            {/* COMMITTEE TARGET BREAKDOWN SECTION */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2">
                <div>
                  <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center space-x-1.5">
                    <Building className="w-4 h-4 text-emerald-600" />
                    <span>Committee Target Breakdown</span>
                  </h2>
                  <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                    Set target value per Committee for period 📅 {getCycleRangeText()}
                  </p>
                </div>

                <div className="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-slate-500">Total Committee Allocated:</span>
                  <span className="text-xs font-black text-slate-900">{formatCurrencyInput(totalCommitteeSum) || '₹0'}</span>
                </div>
              </div>

              {/* Committee List Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {(selectedCommittee ? [selectedCommittee] : committeeList).map((cName, idx) => {
                  const currentVal = getCurrentCommitteeTarget(cName);
                  return (
                    <div
                      key={cName}
                      className="p-3.5 px-4 bg-slate-50/70 hover:bg-slate-50 rounded-xl border border-slate-200/90 transition-all flex items-center justify-between gap-3 shadow-sm"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-900 text-white font-black text-xs flex items-center justify-center shadow-sm">
                          {cName.charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs font-black text-slate-900">{cName}</p>
                          <span className="text-[10px] text-slate-500 font-semibold">Committee Target</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1.5 bg-white px-3 py-1.5 rounded-lg border border-slate-300 shadow-sm focus-within:ring-2 focus-within:ring-slate-900">
                        {/* YEARLY: Clear/Delete disabled for now — uncomment to re-enable */}
                        {currentVal > 0 && targetFrequency !== 'YEARLY' && (
                          <button
                            type="button"
                            onClick={() => setConfirmModalConfig({ isOpen: true, type: 'SINGLE_COMMITTEE', committeeName: cName })}
                            className="text-[10px] font-bold text-rose-600 hover:text-rose-800 hover:bg-rose-50 px-1.5 py-0.5 rounded transition-all cursor-pointer mr-1"
                            title={`Clear target for ${cName}`}
                          >
                            Clear
                          </button>
                        )}
                        <span className="text-[11px] font-bold text-slate-400">Target</span>
                        <input
                          type="text"
                          value={formatCurrencyInput(currentVal)}
                          onChange={e => handleCommitteeTargetChange(cName, parseCurrencyValue(e.target.value))}
                          placeholder="e.g. ₹10,00,000"
                          className="w-32 text-xs font-black text-slate-900 focus:outline-none text-right"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 
            =========================================================================================
            MONTHLY TARGET CARDS GRID (COMMENTED OUT AS REQUESTED - CAN BE RE-ENABLED IF NEEDED)
            =========================================================================================
            <div className="space-y-3">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider px-1">
                <span>Monthly Target Breakdown ({monthlyTargets.length} Months in Cycle)</span>
                <span>Monthly Target & Custom Weeks</span>
              </div>

              <div className="grid grid-cols-1 gap-2.5">
                {monthlyTargets.map((m, idx) => {
                  const mYear = m.year || selectedStartYear;
                  const monthName = `${MONTH_NAMES[m.month - 1]} ${mYear}`;
                  const key = `${mYear}-${m.month}`;
                  const isExpanded = expandedMonthKey === key;
                  const weeks = m.weeklyTargets || [];
                  const weeklySum = weeks.reduce((sum, w) => sum + (Number(w.targetAmount) || 0), 0);
                  const isWeeklyExceeded = m.targetAmount > 0 && weeklySum > m.targetAmount;

                  return (
                    <div
                      key={key}
                      className={`bg-white rounded-xl border transition-all shadow-sm relative ${isExpanded ? 'z-20' : 'z-10'} ${isWeeklyExceeded ? 'border-rose-400 ring-1 ring-rose-300' : 'border-slate-200/80 hover:border-slate-300'
                        }`}
                    >
                      <div className="p-3 px-4 bg-slate-50/80 hover:bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-t-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex flex-col items-center justify-center shadow-sm shrink-0">
                            <span className="text-[9px] font-black text-emerald-400 uppercase leading-none">#{idx + 1}</span>
                            <span className="text-[10px] font-extrabold leading-none mt-0.5">{MONTH_SHORT[m.month - 1]}</span>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-black text-slate-900">{monthName}</p>
                              <span className="text-[9px] bg-slate-200 text-slate-700 font-bold px-1.5 py-0.5 rounded-md">
                                {idx + 1} of {monthlyTargets.length}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 font-medium">
                              {weeks.length > 0
                                ? `${weeks.length} Custom Weekly Period${weeks.length !== 1 ? 's' : ''}`
                                : 'No weekly breakdown defined'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 self-end sm:self-auto">
                          <div className="flex items-center space-x-1.5 bg-white px-3 py-1.5 rounded-lg border border-slate-300 shadow-sm focus-within:ring-2 focus-within:ring-slate-900">
                            <span className="text-[11px] font-bold text-slate-400">Target</span>
                            <input
                              type="text"
                              value={formatCurrencyInput(m.targetAmount)}
                              onChange={e => handleMonthTargetChange(m.month, mYear, parseCurrencyValue(e.target.value))}
                              placeholder="e.g. ₹10,00,000"
                              className="w-32 text-xs font-black text-slate-900 focus:outline-none text-right"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => setExpandedMonthKey(isExpanded ? null : key)}
                            className={`px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-all cursor-pointer flex items-center space-x-1 ${isExpanded
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white hover:bg-slate-100 text-slate-700 border-slate-300'
                              }`}
                          >
                            <Layers className="w-3 h-3 text-emerald-400" />
                            <span>{isExpanded ? 'Hide Weeks' : 'Weeks'}</span>
                            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            */}
          </div>
        )}
      </div>

      {/* Delete Target Cycle Confirmation Modal */}
      {showDeleteModal && targetToDelete && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-150 space-y-4">
            <div className="flex items-center space-x-3 text-rose-600">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900">Delete Target Cycle?</h3>
                <p className="text-xs text-slate-500 font-medium">This action cannot be undone.</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 font-semibold leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200">
              Are you sure you want to permanently delete the target configuration for <strong className="text-slate-900">{targetToDelete.committeeName}</strong>? Only this frequency&apos;s target will be cleared on the DOM 680 Dashboard — other week/month/year targets stay as-is.
            </p>

            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTargetToDelete(null);
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (targetToDelete) {
                    deleteMutation.mutate(targetToDelete.id);
                    setShowDeleteModal(false);
                    setTargetToDelete(null);
                  }
                }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-black rounded-xl transition-all shadow-sm cursor-pointer disabled:opacity-50 flex items-center space-x-1"
              >
                {deleteMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />}
                <span>{deleteMutation.isPending ? 'Deleting...' : 'Delete Target Cycle'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CONFIRMATION MODAL POPUP FOR CLEARING SINGLE / ALL COMMITTEE TARGETS */}
      {confirmModalConfig?.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-sm w-full p-5 space-y-4 animate-in zoom-in-95 duration-150">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900">
                  {confirmModalConfig.type === 'SINGLE_COMMITTEE'
                    ? `Clear ${confirmModalConfig.committeeName} Target?`
                    : 'Clear All Targets?'}
                </h3>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  Period: 📅 {getCycleRangeText()}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed font-semibold">
              {confirmModalConfig.type === 'SINGLE_COMMITTEE' ? (
                <>
                  Are you sure you want to clear/delete the target for <strong className="text-slate-900 font-black">{confirmModalConfig.committeeName}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to delete and clear <strong className="text-slate-900 font-black">ALL committee targets</strong> for this cycle?
                </>
              )}
            </p>

            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmModalConfig(null)}
                className="px-3.5 py-2 rounded-xl text-xs font-extrabold text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (confirmModalConfig.type === 'SINGLE_COMMITTEE' && confirmModalConfig.committeeName) {
                    const cName = confirmModalConfig.committeeName;
                    handleCommitteeTargetChange(cName, 0);
                    await handleSavePlanner({ [cName]: 0 });

                    setSuccessMsg(`Target for ${cName} cleared successfully!`);
                    setTimeout(() => setSuccessMsg(null), 3000);
                  } else if (confirmModalConfig.type === 'CLEAR_ALL') {
                    const zeroOverrides: Record<string, number> = {};
                    const activeComms = committeeList && committeeList.length > 0 ? committeeList : ['Engineering', 'Finance', 'Liaison', 'Legal'];
                    for (const cName of activeComms) {
                      zeroOverrides[cName] = 0;
                    }

                    setWeeklyCommitteeTargets(zeroOverrides);
                    setMonthlyCommitteeTargets(zeroOverrides);
                    setYearlyCommitteeTargets(zeroOverrides);
                    setYearlyTarget(0);
                    setMonthlyTargets(prev => prev.map(m => ({ ...m, targetAmount: 0, weeklyTargets: [] })));

                    try {
                      await handleSavePlanner(zeroOverrides);
                    } catch (e) {}

                    const matchingCycles = (cyclesList || []).filter(c => {
                      const cFreq = c.targetFrequency || (c.monthsCount === 12 ? 'YEARLY' : 'MONTHLY');
                      if (cFreq !== targetFrequency) return false;
                      if (targetFrequency === 'WEEKLY') {
                        return c.weeklyStartDate === weeklyStartDate && c.weeklyEndDate === weeklyEndDate;
                      } else if (targetFrequency === 'MONTHLY') {
                        return c.startMonth === selectedStartMonth && c.startYear === selectedStartYear;
                      } else {
                        // Match planner period OR master cycle (Clear All must hit yearly rows)
                        return (
                          (c.startMonth === selectedStartMonth && c.startYear === selectedStartYear) ||
                          (c.startMonth === masterStartMonth && c.startYear === masterStartYear)
                        );
                      }
                    });

                    for (const cycleItem of matchingCycles) {
                      try {
                        await apiClient.delete(`/admin/reports/dom680-target-config/${cycleItem.id}`);
                      } catch (e) {}
                    }

                    await refetchCyclesList();
                    queryClient.invalidateQueries({ queryKey: ['dom680TrackerReport'] });
                    queryClient.invalidateQueries({ queryKey: ['dom680TargetConfig'] });

                    setSuccessMsg('All committee targets cleared and deleted successfully!');
                    setTimeout(() => setSuccessMsg(null), 3000);

                    // Auto move back to Cycles Registry list screen
                    setViewMode('REGISTRY');
                  }
                  setConfirmModalConfig(null);
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded-xl transition-all shadow-sm cursor-pointer active:scale-95"
              >
                {confirmModalConfig.type === 'SINGLE_COMMITTEE' ? 'Yes, Clear Target' : 'Yes, Delete All Targets'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
